import { Node } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import {
  File,
  FileArchive,
  FileAudio,
  FileText,
  FileVideo,
  Image,
} from 'lucide-react';
import { Popover } from 'ui/components';
import FileCardPopover from './FileCardPopover';

const InlineFileCardComponent = ({ node, getPos, editor }: NodeViewProps) => {
  const { name, type, size, url } = node.attrs;

  function initiateDownload(url: string, fileName: string) {
    // Open the URL in a new tab
    window.open(url, '_blank');
  }

  const getFileIcon = () => {
    if (type.startsWith('image/'))
      return <Image className="size-6 text-blue-500" />;
    if (type.startsWith('audio/'))
      return <FileAudio className="size-6 text-green-500" />;
    if (type.startsWith('video/'))
      return <FileVideo className="size-6 text-red-500" />;
    if (type.includes('compressed') || type.includes('zip'))
      return <FileArchive className="size-6 text-yellow-500" />;
    if (type.includes('text') || type.includes('document'))
      return <FileText className="size-6 text-purple-500" />;
    return <File className="size-6 text-gray-500" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
    else return (bytes / 1073741824).toFixed(1) + ' GB';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const convertToBlock = () => {
    if (typeof getPos !== 'function') {
      console.error('getPos is not a function');
      return;
    }

    const pos = getPos();
    const { tr } = editor.view.state;
    const $pos = tr.doc.resolve(pos);

    // Create the block node
    const blockNode = editor.schema.nodes.blockFileCard.create(node.attrs);

    // Find the start and end of the current paragraph
    const start = $pos.before();
    const end = $pos.after();

    // Get the content before and after the inline file card
    const beforeContent = $pos.parent.textBetween(0, $pos.parentOffset, '\n');
    const afterContent = $pos.parent.textBetween(
      $pos.parentOffset + node.nodeSize,
      $pos.parent.content.size,
      '\n'
    );

    // Create new paragraphs for content before and after if they exist
    const newNodes: Node[] = [];

    if (beforeContent) {
      const beforeParagraph = editor.schema.nodes.paragraph.create(
        null,
        editor.schema.text(beforeContent)
      );
      newNodes.push(beforeParagraph);
    }

    newNodes.push(blockNode);

    if (afterContent) {
      const afterParagraph = editor.schema.nodes.paragraph.create(
        null,
        editor.schema.text(afterContent)
      );
      newNodes.push(afterParagraph);
    }

    // Replace the current paragraph with the new structure
    tr.replaceWith(start, end, newNodes);

    // Calculate the position after the new block node
    const newPos =
      start +
      newNodes.slice(0, -1).reduce((sum, node) => sum + node.nodeSize, 0) +
      1;

    // Set the selection after the new block node using TextSelection
    tr.setSelection(TextSelection.create(tr.doc, newPos));

    editor.view.dispatch(tr);
  };

  return (
    <NodeViewWrapper as="span" data-drag-handle>
      <Popover>
        <Popover.Trigger asChild>
          <span className="inline-flex items-center space-x-1 rounded border border-border bg-background px-1 py-0.5 text-sm hover:bg-gray-50">
            {getFileIcon()}
            <span className="font-medium text-gray-900">{name}</span>
            <span className="text-xs text-gray-500">
              ({formatFileSize(size)})
            </span>
          </span>
        </Popover.Trigger>
        <Popover.Content className="p-2">
          <FileCardPopover
            url={url}
            view="inline"
            switchView={convertToBlock}
          />
        </Popover.Content>
      </Popover>
    </NodeViewWrapper>
  );
};

export default InlineFileCardComponent;
